import Layout from '@/components/Layout/Vanilla';
import Button from '@/components/Button';
import Hero from '@/components/Hero';
import SectionHero from '@/components/SectionHero';
import IndustryList from '@/components/IndustryList';
import ContentBlock from '@/components/ContentBlock';
import TestimonialCard from '@/components/TestimonialCard';
import USPGrid from '@/components/USPGrid';
import CardGrid from '@/components/CardGrid';
import { DefaultLogoReel } from '@/components/LogoReel';
import Section from '@/components/Section';
import { fetchPage, gql } from '@/lib/graphcms';

export default function ComposableArchitectures({
  page,
  caseStudies,
  resources,
}) {
  const { usps, contentBlocks } = page;

  return (
    <Layout seo={page}>
      <Section>
        <Hero {...page} alignment="left">
          <Button
            href="/resources/dxp-report-2020"
            appearance="outline"
            colour="pink"
            size="large"
          >
            Download the report &#8594;
          </Button>
        </Hero>
      </Section>
      <DefaultLogoReel />
      <Section>
        <SectionHero
          title="Composable architectures enable composable businesses"
          description="If you’re not modernizing, innovating, or adapting to newer approaches in delivering better digital experiences with modern services, you'll struggle to remain competitive."
        />

        <USPGrid title={false} items={usps} />
      </Section>
      <Section>
        <SectionHero
          title="Where to start?"
          description="Lower IT costs, service flexibility, scalability, optimized business operations, and serurity & governance are only a few of the many benefits composable architectures offer."
        />
        <IndustryList entries={contentBlocks} Component={ContentBlock} />
      </Section>
      <Section colour="light">
        <SectionHero
          title="See how teams use composable architectures to scale"
          description="From high-velocity scaleups to the Fortune 500, global businesses rely on GraphCMS to unify APIs and deliver better digital products."
          ctas={[
            { href: '/case-studies', label: 'View customer success stories' },
          ]}
        />
        <IndustryList entries={caseStudies} Component={TestimonialCard} />
      </Section>
      <Section>
        <SectionHero
          title="Resources to help you succeed"
          description="Get up to speed with implementing a scalable architecture with our carefully crafted studies and experiences"
          ctas={[{ href: '/resources', label: 'Download GraphCMS resources' }]}
        />
        <CardGrid entries={resources} linkSubpath="resources" />
      </Section>
    </Layout>
  );
}

export const getStaticProps = async ({ preview = false }) => {
  const { page, resources, caseStudies } =
    (await fetchPage(
      gql`
        fragment composableArchitecturesAuthorData on Author {
          name
          title
          bio
          photo {
            ...assetDataXL
          }
        }

        fragment composableArchitecturesResourceData on Resource {
          id
          slug
          title
          subTitle
          coverImage {
            ...assetDataXL
          }
          speakers {
            ...composableArchitecturesAuthorData
          }
          content
          externalUrl
          location
          starts
          type
        }

        fragment composableArchitecturesCaseStudyData on CaseStudy {
          id
          slug
          title
          subTitle
          coverImage {
            id
            url
            width
            height
            fileName
            alt
          }
          excerpt
          content
          externalUrl
          company {
            name
            url
            logo {
              ...assetDataXL
            }
          }
          customer {
            ...composableArchitecturesAuthorData
          }
        }

        query composableArchitecturesPageQuery {
          page(where: { slug: "composable-architectures" }) {
            ...pageData # Fragment is inside the method
          }
          resources(
            where: {
              id_in: ["ckggeebmw1los0b538d1myi7g", "cklnklfc8x4wl0b55i5ci8p9w"]
            }
          ) {
            ...composableArchitecturesResourceData
          }
          caseStudies(
            where: {
              id_in: [
                "cknrxrebkeygs0b08rxrsv7xx"
                "ckgs1j1b400rw0a044fk0hpys"
                "ckjwywawg0xbr09094wk7tiai"
              ]
            }
          ) {
            ...composableArchitecturesCaseStudyData
          }
        }
      `,
      {
        preview,
      },
    )) || [];

  return {
    props: {
      preview,
      page,
      resources,
      caseStudies,
    },
    revalidate: 3600,
  };
};
